import React, { ChangeEvent, useEffect, useMemo } from 'react';
import { Card, Checkbox, Popover, Text, VStack, Box } from '@televet/kibble-ui';
import getIsAllPetParentsReachableByPush from 'pages/Conversations/utils/getIsAllPetParentsReachableByPush';
import getIsAllPetParentsReachableBySms from 'pages/Conversations/utils/getIsAllPetParentsReachableBySms';
import { MessageSource, MessageType, useHasDeviceThatCanRecievePushQuery } from 'shared/types/graphql';
import { ChannelViewClinicPetParentFragment } from 'shared/types/graphql';
import { ChannelViewChannelMessageFragment } from 'shared/types/graphql';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import useClinicUser from 'shared/hooks/useClinicUser';

interface SendAsChatCheckboxProps {
  channelPetParentIds: string[];
  channelPetParents: ChannelViewClinicPetParentFragment[];
  channelId?: string;
  channelLastMessage: ChannelViewChannelMessageFragment | null;
  setSendAsChatMessage: (sendAsChatMessage: boolean) => void;
  sendAsChatMessage: boolean;
  messageType: MessageType;
  isTeamChannel: boolean;
  draftMessageText: string;
  inputTextareaRef: React.RefObject<HTMLTextAreaElement>;
}

const SendAsChatCheckbox = ({
  channelPetParentIds,
  channelPetParents,
  channelId,
  channelLastMessage,
  setSendAsChatMessage,
  sendAsChatMessage,
  messageType,
  isTeamChannel,
  draftMessageText,
  inputTextareaRef,
}: SendAsChatCheckboxProps): JSX.Element => {
  const { currentClinicId, currentClinic, clinicUser } = useClinicUser();

  const { data: deviceData } = useHasDeviceThatCanRecievePushQuery({
    variables: {
      clinicPetParentIds: channelPetParentIds,
    },
    skip: !channelPetParentIds,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    nextFetchPolicy: GraphQLFetchPolicies.CacheFirst,
  });

  const { shouldDisable: isPushEnabled, enabledPetParentsLength } =
    getIsAllPetParentsReachableByPush(channelPetParents);

  const isAllPetParentsReachableBySms = getIsAllPetParentsReachableBySms(channelPetParents);

  const showCheckbox =
    !isTeamChannel && Boolean(currentClinic?.hasSmsConversations) && messageType === MessageType.Message;

  const currentUserDisplayName =
    clinicUser?.nameDisplay || [clinicUser?.firstName, clinicUser?.lastName].join(' ').trim();

  const isFeatureEnabledAndHasPushEnabled = isPushEnabled && !!deviceData?.findFirstClinicPetParentDevice;

  const shouldDisable = isFeatureEnabledAndHasPushEnabled || !isAllPetParentsReachableBySms;

  const tooltipText = useMemo((): string => {
    // If the checkbox is disabled, return the reason why
    if (shouldDisable) {
      const disabledMessagePrefix = 'Messages sent in this conversation will be sent as a chat message because';

      if (!isFeatureEnabledAndHasPushEnabled) {
        return `${disabledMessagePrefix} one or more clients in this conversation have a primary phone number that does not accept SMS text messages.`;
      }

      if (enabledPetParentsLength > 1) {
        return `${disabledMessagePrefix} one or more clients in this conversation have installed the mobile app and opted in to app notifications.`;
      }

      return `${disabledMessagePrefix} this client has installed the mobile app and opted in to app notifications.`;
    }

    // If the checkbox is enabled, return the message preview
    return `With "Send as chat" ${
      sendAsChatMessage
        ? ' enabled, clients will receive a link to view and reply to this message from the web link:'
        : ' disabled, clients will receive this as a direct text message:'
    }`;
  }, [isFeatureEnabledAndHasPushEnabled, enabledPetParentsLength, sendAsChatMessage, shouldDisable]);

  const messagePreviewContent = useMemo(() => {
    const petParentName = [channelPetParents[0]?.firstName, channelPetParents[0]?.lastName].join(' ').trim();
    const clinicName = currentClinic?.name || 'your clinic';
    const draftMessage = draftMessageText || '(Your message here)';
    return `Hi ${petParentName}! You have a new message in your chat from ${currentUserDisplayName} with ${clinicName}:\n
              "${draftMessage}"\n
              Please follow this link to view this message in your conversation: https://tele.vet/xxxxxxx`;
  }, [channelPetParents, currentClinic, currentUserDisplayName, draftMessageText]);

  // Hardcode value for Avian and Animal Hospital
  useEffect(() => {
    if (currentClinicId === 'ckyt3x2tqf3mq08861cl16h6n') {
      setSendAsChatMessage(true);
    }
  }, [currentClinicId, setSendAsChatMessage]);

  /**
   * Sync "Send as chat message" checkbox with the platform
   * from which the last message was sent (web vs. SMS)
   */
  useEffect(() => {
    // If all parents in the channel are not reachable by SMS, or the channel is a team channel, we should always send as chat
    if (!isAllPetParentsReachableBySms || isTeamChannel) {
      setSendAsChatMessage(true);
      return;
    }

    // If the last message was sent by a pet parent in th pet portal, or the pet parent doesn't have sms notifications enabled
    if (channelLastMessage?.author?.clinicPetParent?.id) {
      const isPetPortalMessage = channelLastMessage.source === MessageSource.Web;
      const isSmsNotificationEnabled = channelLastMessage.author?.clinicPetParent?.petParentSetting?.smsNotifications;
      setSendAsChatMessage(isPetPortalMessage || !isSmsNotificationEnabled);
      return;
    }

    // If the last message was a pet portal request, we should always send as chat
    if (channelLastMessage?.attachments?.length) {
      const isPetPortalRequest = channelLastMessage.attachments.some(
        (attachment) => attachment.attachmentType === 'PetParentRequest',
      );
      setSendAsChatMessage(isPetPortalRequest);
      return;
    }

    setSendAsChatMessage(false);
  }, [channelId, channelLastMessage, isAllPetParentsReachableBySms, isTeamChannel, setSendAsChatMessage]);

  if (!showCheckbox) return <></>;

  return (
    <Popover
      trigger="hover"
      placement="top-start"
      size="lg"
      isLazy
      anchor={
        <Box>
          <Checkbox
            name="sendAsChatMessage"
            isChecked={shouldDisable || sendAsChatMessage}
            isDisabled={shouldDisable}
            isFieldInline
            data-mixpanel-name="Send as chat message button"
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setSendAsChatMessage(e.target.checked);
              inputTextareaRef?.current?.focus();
            }}
          >
            Send as chat
          </Checkbox>
        </Box>
      }
    >
      <VStack w="100%">
        <Text size="sm" variant="onContrast">
          {tooltipText}
        </Text>
        <Card bg="backgroundContrast" size="sm" isFullWidth w="100%">
          <Text size="sm" variant="onContrast" whiteSpace="pre-line">
            {sendAsChatMessage
              ? messagePreviewContent
              : `${draftMessageText || '(Your message here)'}
                - ${currentUserDisplayName}`}
          </Text>
        </Card>
      </VStack>
    </Popover>
  );
};

export default SendAsChatCheckbox;
